import { useEffect, useState } from 'react'
import { deferToIdle } from 'src/utils/deferToIdle'

/**
 * Returns `true` once the browser has been idle after mount, or after
 * `timeoutMs`, whichever comes first — and never before hydration. Unlike
 * `useDelayedLoad`, a user interaction does NOT trigger it: a tap that
 * triggered a 100 kB script download would land that script's evaluation
 * right inside the tap's own INP window, which is the collision this hook
 * exists to avoid.
 *
 * Use for third-party libraries whose calls are already queued by an inline
 * stub (gtag's `dataLayer`, HubSpot's `_hsq`): nothing is lost while they
 * wait, the queue replays on load, and the main-thread cost of evaluating
 * them moves out of the seconds right after hydration where a cheap Android
 * takes its first tap. Safari has no `requestIdleCallback`, so `deferToIdle`
 * falls back to `setTimeout(0)` there — iOS is not where the cost is.
 */
export function useIdleLoad(timeoutMs: number): boolean {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (ready) return
    return deferToIdle(() => setReady(true), timeoutMs)
  }, [ready, timeoutMs])

  return ready
}

export default useIdleLoad
